import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client.js';
import type { AppDevice, AppRegistration, ExtensionRegistrations } from '@arcnet/contracts';
import type {
  Extension, Device, VoicemailBox, RecordingPolicy,
} from '@arcnet/contracts';

interface ListResponse<T> { data: T[] }

export const extKeys = {
  extension: (t: string, id: string) => ['extension', t, id] as const,
  devices: (t: string) => ['devices', t] as const,
  recordingPolicy: (t: string) => ['recording-policy', t] as const,
  adminGroups: (t: string) => ['admin-groups', t] as const,
  voicemail: (t: string, id: string) => ['voicemail', t, id] as const,
  appDevices: (t: string, id: string) => ['app-devices', t, id] as const,
  appRegistrations: (t: string, id: string) => ['app-registrations', t, id] as const,
  registrations: (t: string, id: string) => ['registrations', t, id] as const,
};

/**
 * `live` polls while the tab is in the foreground, for the registration view.
 *
 * The node side is already event-driven: FreeSWITCH raises sofia::register,
 * the agent pushes within about a second. This is only the last hop, browser
 * to control plane, and a poll is the right shape for it at this size -- one
 * request every ten seconds per open tab, against a WebSocket's connection
 * state, auth on upgrade, and sticky routing once there is more than one API
 * instance behind the proxy. Revisit when a tenant watches hundreds of
 * extensions at once, which is a wallboard, not this page.
 *
 * refetchIntervalInBackground defaults to false, so a tab left open in another
 * window stops asking.
 */
export const useExtension = (tenantId: string, id: string | null, live = false) =>
  useQuery({
    queryKey: extKeys.extension(tenantId, id ?? ''),
    queryFn: () => api.get<Extension>(`/v1/tenants/${tenantId}/extensions/${id}`),
    enabled: !!id,
    refetchInterval: live ? 10_000 : false,
  });

export const useDevices = (tenantId: string) =>
  useQuery({
    queryKey: extKeys.devices(tenantId),
    queryFn: () => api.get<ListResponse<Device>>(`/v1/tenants/${tenantId}/devices`),
    enabled: !!tenantId,
  });

export const useAdminGroups = (tenantId: string) =>
  useQuery({
    queryKey: extKeys.adminGroups(tenantId),
    queryFn: () => api.get<ListResponse<{ id: string; name: string; code: string | null; extension_count?: number }>>(
      `/v1/tenants/${tenantId}/admin-groups`),
    enabled: !!tenantId,
  });

export const useVoicemail = (tenantId: string, extensionId: string | null) =>
  useQuery({
    // Returns null when the extension has no mailbox — a normal state, so it
    // must not be treated as an error or retried.
    queryKey: extKeys.voicemail(tenantId, extensionId ?? ''),
    queryFn: () => api.get<VoicemailBox | null>(
      `/v1/tenants/${tenantId}/extensions/${extensionId}/voicemail`),
    enabled: !!extensionId,
    retry: false,
  });

export function useSaveVoicemail(tenantId: string, extensionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put(`/v1/tenants/${tenantId}/extensions/${extensionId}/voicemail`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: extKeys.voicemail(tenantId, extensionId) });
      void qc.invalidateQueries({ queryKey: ['extensions', tenantId] });
    },
  });
}

/**
 * Removes the mailbox entirely.
 *
 * Separate from saving with empty fields, which would leave a mailbox that
 * still answers unanswered calls and records into nowhere. Deleting is the only
 * way to make an extension stop taking messages again.
 */
export function useDeleteVoicemail(tenantId: string, extensionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.delete(`/v1/tenants/${tenantId}/extensions/${extensionId}/voicemail`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: extKeys.voicemail(tenantId, extensionId) });
      void qc.invalidateQueries({ queryKey: ['extensions', tenantId] });
      void qc.invalidateQueries({ queryKey: ['voicemail', tenantId] });
    },
  });
}

/**
 * Creating a handset returns its provisioning URL, ONCE.
 *
 * The token behind it is stored only as a hash, so this response is the only
 * chance to see it — and it is the whole point of the device record: without it
 * there is nothing to type into DHCP option 66 or the phone's web interface.
 * An earlier version of this hook discarded the response, which left handset
 * provisioning working perfectly and completely unreachable.
 */
export interface SavedDevice {
  id: string;
  provision_token?: string;
  provisioning_url?: string;
  /**
   * The handset's OWN SIP credential, shown once.
   *
   * A device authenticates as itself rather than as the extension, which is
   * what makes it separately silenceable and separately revocable. The
   * provisioning file carries this automatically; it is surfaced here for the
   * handsets that have to be configured by hand.
   */
  sip_credential?: { username: string; realm: string; password: string };
}

export function useSaveDevice(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? api.patch<SavedDevice>(`/v1/tenants/${tenantId}/devices/${id}`, body)
         : api.post<SavedDevice>(`/v1/tenants/${tenantId}/devices`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: extKeys.devices(tenantId) }); },
  });
}

/**
 * Re-open a handset's provisioning window.
 *
 * A provisioning URL only works while a window is open, or from the address the
 * phone already used it from — so a replacement handset, an office move or a
 * new circuit needs this. It does NOT issue a new token: rotating it would
 * invalidate whatever is already in the customer's DHCP configuration and turn
 * a five-minute job into a site visit.
 */
export function useOpenProvisioning(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ open_minutes: number }>(
        `/v1/tenants/${tenantId}/devices/${id}/provisioning/open`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: extKeys.devices(tenantId) }); },
  });
}

export function useDeleteDevice(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/tenants/${tenantId}/devices/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: extKeys.devices(tenantId) }); },
  });
}

export function useSaveAdminGroup(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; code?: string }) =>
      api.post<{ id: string }>(`/v1/tenants/${tenantId}/admin-groups`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: extKeys.adminGroups(tenantId) }); },
  });
}

/** Renames a department, or changes its code. */
export function useUpdateAdminGroup(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; code: string | null }) =>
      api.patch(`/v1/tenants/${tenantId}/admin-groups/${id}`, body),
    // Extensions carry the department's name, so a rename shows there too.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: extKeys.adminGroups(tenantId) });
      void qc.invalidateQueries({ queryKey: ['extensions', tenantId] });
      void qc.invalidateQueries({ queryKey: ['extension', tenantId] });
    },
  });
}

/** Deletes a department. Refused while extensions are in it. */
export function useDeleteAdminGroup(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/tenants/${tenantId}/admin-groups/${id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: extKeys.adminGroups(tenantId) }); },
  });
}

// ---------------------------------------------------------- recording policy
export const useRecordingPolicy = (tenantId: string) =>
  useQuery({
    queryKey: extKeys.recordingPolicy(tenantId),
    queryFn: () => api.get<RecordingPolicy>(`/v1/tenants/${tenantId}/recording-policy`),
    enabled: !!tenantId,
  });

export function useSaveRecordingPolicy(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put(`/v1/tenants/${tenantId}/recording-policy`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: extKeys.recordingPolicy(tenantId) });
    },
  });
}

/**
 * Applies a recording mode to a chosen set of extensions.
 *
 * Invalidates the extension list because the change is visible there, and the
 * per-extension detail because a seat open in another tab is now stale.
 */
export function useApplyRecording(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { extension_ids: string[]; record_mode: string }) =>
      api.post<{ updated: number }>(`/v1/tenants/${tenantId}/recording-policy/apply`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['extensions'] });
      void qc.invalidateQueries({ queryKey: ['extension', tenantId] });
    },
  });
}

/** The phones an extension's ArcSIP app is signed in on (docs/34). */
export const useAppDevices = (tenantId: string, extensionId: string) =>
  useQuery({
    queryKey: extKeys.appDevices(tenantId, extensionId),
    queryFn: () => api.get<{ data: AppDevice[] }>(`/v1/tenants/${tenantId}/extensions/${extensionId}/app-devices`),
    enabled: !!tenantId && !!extensionId,
    // A phone signing in shows up while the code is still on screen.
    refetchInterval: 10_000,
  });

/** The extension's registrations a phone can sign in with, main first (docs/34). */
export const useAppRegistrations = (tenantId: string, extensionId: string) =>
  useQuery({
    queryKey: extKeys.appRegistrations(tenantId, extensionId),
    queryFn: () => api.get<{ data: AppRegistration[] }>(
      `/v1/tenants/${tenantId}/extensions/${extensionId}/app-registrations`),
    enabled: !!tenantId && !!extensionId,
  });

/** A one-time sign-in code for the app, shown as a QR code: ten minutes, one use. */
export function useCreateAppCode(tenantId: string, extensionId: string) {
  return useMutation({
    mutationFn: (registrationId: string | null) => api.post<{
      uri: string; username: string; expires_at: string; minutes: number;
    }>(`/v1/tenants/${tenantId}/extensions/${extensionId}/app-codes`,
      registrationId ? { registration_id: registrationId } : {}),
  });
}

export function useSignOutAppDevice(tenantId: string, extensionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) =>
      api.delete<void>(`/v1/tenants/${tenantId}/extensions/${extensionId}/app-devices/${deviceId}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: extKeys.appDevices(tenantId, extensionId) }); },
  });
}

/** An extension's named additional registrations (contracts registrations.ts). */
export const useRegistrations = (tenantId: string, extensionId: string) =>
  useQuery({
    queryKey: extKeys.registrations(tenantId, extensionId),
    queryFn: () => api.get<ExtensionRegistrations>(`/v1/tenants/${tenantId}/extensions/${extensionId}/registrations`),
    enabled: !!tenantId && !!extensionId,
  });

type SipCredentialShown = { username: string; realm: string; password: string };

export function useRegistrationActions(tenantId: string, extensionId: string) {
  const qc = useQueryClient();
  const base = `/v1/tenants/${tenantId}/extensions/${extensionId}/registrations`;
  const done = () => {
    void qc.invalidateQueries({ queryKey: extKeys.registrations(tenantId, extensionId) });
    void qc.invalidateQueries({ queryKey: extKeys.appRegistrations(tenantId, extensionId) });
    void qc.invalidateQueries({ queryKey: extKeys.appDevices(tenantId, extensionId) });
  };
  return {
    add: useMutation({
      mutationFn: (name: string) => api.post<{ username: string; sip_credential: SipCredentialShown }>(base, { name }),
      onSuccess: done,
    }),
    ring: useMutation({
      mutationFn: ({ id, ring_enabled }: { id: string; ring_enabled: boolean }) =>
        api.patch(`${base}/${id}`, { ring_enabled }),
      onSuccess: done,
    }),
    password: useMutation({
      mutationFn: (id: string) => api.post<{ sip_credential: SipCredentialShown }>(`${base}/${id}/password`, {}),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete<void>(`${base}/${id}`),
      onSuccess: done,
    }),
  };
}

/**
 * Whether one feature is on for a seat, as the Features tab resolves it: the seat's type, plus any
 * override. `undefined` while it is still being read, so a caller can wait rather than flash.
 *
 * Same query key as the Features tab (ExtensionFeaturesTab), so turning a feature on there is
 * reflected wherever else it is asked about, without a second request.
 */
export function useExtensionFeature(tenantId: string, extensionId: string, feature: string): boolean | undefined {
  const q = useQuery({
    queryKey: ['tenants', tenantId, 'extensions', extensionId, 'features'] as const,
    queryFn: () => api.get<{ data: Array<{ key: string; enabled: boolean }> }>(
      `/v1/tenants/${tenantId}/extensions/${extensionId}/features`),
  });
  if (!q.data) return undefined;
  return q.data.data.some((f) => f.key === feature && f.enabled);
}
